import React, { useContext, useEffect } from 'react'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import classNames from 'classnames'
import MainNavigationContext from '@toasttab/shared/src/data/HeaderNavigation/context/MainNavigationContext'
import SectionCTA from '@toasttab/shared/src/components/organisms/Navigation/Mobile/ctas/SectionCTA'
import DemoCTA from '@toasttab/shared/src/components/organisms/Navigation/Mobile/ctas/DemoCTA'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import {
  ShowcaseEntryProps,
  SubMenuProps,
  SubMenuEntryProps
} from '@toasttab/shared/src/data/HeaderNavigation/MainNavigationMenu.d'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { I18NextContext } from '@toasttab/shared/src/@types/types'

const generateSubMenuSectionShowcases = (
  showcase: ShowcaseEntryProps,
  showMobile: boolean,
  localizationContext: I18NextContext
) => {
  const showcaseHref = localizeLink(
    showcase.url,
    localizationContext.language,
    localizationContext
  )
  if (showMobile) {
    return (
      <div
        className={`flex flex-nowrap w-full px-4 pb-6 mb-6 border-b border-gray-25 ${showcase.icon} before:w-6 before:h-6 before:mr-3`}
        key={`mobile-sub-showcase-${showcase.title}}`}
      >
        <a className={showcase.heapClass} role='menuitem' href={showcaseHref}>
          <div className='inline-flex items-center justify-center font-sansPro text-base text-default font-semibold capitalize after:content-none'>
            {showcase.title}
            {showcase.badge && (
              <Badge
                badgeColor={showcase.badge.color}
                classes='text-[8px] leading-[14px] tracking-[1px] py-[2px] ml-3'
                text={showcase.badge.label}
                uppercase
              />
            )}
          </div>
          <p className='font-sansPro text-base text-secondary'>
            {showcase.description}
          </p>
        </a>
      </div>
    )
  }
}

const generateArticleEntries = (
  label: string,
  entry: SubMenuEntryProps,
  localizationContext: I18NextContext
) => {
  const articleHref = localizeLink(
    entry.url,
    localizationContext.language,
    localizationContext
  )
  return (
    <li
      className={`flex flex-nowrap pb-6 mb-6 -mx-4 px-4 border-b border-gray-25 ${entry.icon} before:w-6 before:h-6 before:mr-3`}
      role='none'
      key={`mobile-sub-article-${entry.label}}`}
    >
      <a className={entry.heapClass} role='menuitem' href={articleHref}>
        <p className='font-sansPro text-base text-default font-semibold'>
          {label}
        </p>
        <p className='font-sansPro text-base text-secondary font-normal'>
          {entry.label}
        </p>
      </a>
    </li>
  )
}

const generateLinkEntries = (
  parent: string,
  entry: SubMenuEntryProps,
  localizationContext: I18NextContext
) => {
  const linkHref = localizeLink(
    entry.url,
    localizationContext.language,
    localizationContext
  )

  return (
    <li
      className={`font-sansPro text-base font-semibold mb-6 ${entry.icon} before:w-6 before:h-6 before:relative before:top-2 before:mr-3`}
      role='none'
      key={`mobile-sub-link-${entry.label}}`}
    >
      <a
        className={`${entry.heapClass} inline-flex items-center justify-center text-default font-normal`}
        role='menuitem'
        href={linkHref}
      >
        {entry.label}{' '}
        {entry.badge && (
          <Badge
            badgeColor={entry.badge.color}
            classes='text-[10px] leading-[14px] tracking-[1px] py-[2px] ml-3'
            text={entry.badge.label}
            uppercase
          />
        )}
      </a>
    </li>
  )
}

export const calcSubMenuHeight = (windowSize: any) => {
  const headerEl = document?.getElementsByTagName('header')
  const subMobileELs =
    document?.querySelectorAll<HTMLElement>('.mobile-sub-nav')
  if (windowSize.width <= 767) {
    subMobileELs.forEach((subMobileEL) => {
      subMobileEL.style.height = '100vh'
    })
  } else if (768 <= windowSize.width && windowSize.width < 992) {
    subMobileELs.forEach((subMobileEL) => {
      subMobileEL.style.height = `calc(100vh - ${headerEl[0].offsetHeight}px)`
      subMobileEL.style.top = `${headerEl[0].offsetHeight}px`
    })
  }
}

const generateSubMenuSections = (
  parent: string,
  menu: SubMenuProps,
  localizationContext: I18NextContext
) => {
  const sectionCss = classNames('col-span-2 px-4 mb-6', {
    'border-b border-gray-0': menu.showLabelMobile
  })

  return (
    <div className={sectionCss} key={`mobile-sub-link-${menu.label}}`}>
      {menu.showLabelMobile && (
        <h5 className='font-sansPro font-normal text-base text-secondary mb-6'>
          {menu.label}
        </h5>
      )}
      <ul>
        {menu.subMenuEntries.map((entries) =>
          menu.subMenuEntryType === 'articles'
            ? generateArticleEntries(menu.label, entries, localizationContext)
            : generateLinkEntries(parent, entries, localizationContext)
        )}
      </ul>
    </div>
  )
}

export const SubMenus = ({ navigationData }: any) => {
  const {
    selectedParentMenu,
    mobileNavOpen,
    setSelectedParentMenu,
    setMobileNavOpen
  } = useContext(MainNavigationContext)
  const localizationContext = useContext(I18nextContext)
  const windowSize = useWindowSize()

  useEffect(() => {
    window.addEventListener('resize', () => calcSubMenuHeight(windowSize))

    calcSubMenuHeight(windowSize)
  }, [windowSize.width])

  const getSubMenuCss = (parent: string) => {
    return classNames(
      'bg-white h-100vh overflow-y-scroll !no-scrollbar fixed md:py-8 top-[0px] md:top-auto z-[100] md:z-[90] transition-right md:transition-left ease-out !duration-500 md:border-r md:border-gray-25 w-full md:max-w-[375px] min-[992px]:hidden',
      {
        'right-[-800px] md:left-[-500px]':
          parent != selectedParentMenu ||
          !mobileNavOpen ||
          windowSize.width > 768,
        'right-0 md:left-[375px] pb-40':
          parent == selectedParentMenu &&
          mobileNavOpen &&
          windowSize.width < 992
      }
    )
  }

  const toggleMobileMenu = () => {
    setMobileNavOpen(!mobileNavOpen)
    setSelectedParentMenu(null)
  }

  return (
    <>
      {navigationData.parentMenus.map((menu: any) => {
        if (menu.subMenus) {
          return (
            <nav
              id={`${menu.label.replace(/\s/g, '-')}-mobile-menu`}
              className={`mobile-sub-nav w-screen ${getSubMenuCss(menu.label)}`}
              aria-label={`Toast ${menu.label} Dropdown Menu`}
              key={`mobile-sub-menu-${menu.label}}`}
              style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
            >
              <div className='flex md:hidden flex-nowrap justify-between items-center border-b border-gray-25 px-4 py-2 mb-10'>
                <button
                  className='before:content-icon-chevron-left before:h-6 before:w-6 relative top-1'
                  onClick={() => setSelectedParentMenu(null)}
                  aria-label='Return to Previous Menu'
                />
                <h4 className='font-effra text-[22px] leading-8'>
                  {menu.label}
                </h4>
                <button
                  aria-label='Close Mobile Navigation'
                  className='w-5 h-5'
                  onClick={toggleMobileMenu}
                >
                  <span
                    aria-hidden='true'
                    className='block absolute h-0.5 w-5 bg-gray-125 transform transition duration-500 ease-in-out rotate-45'
                  />
                  <span
                    aria-hidden='true'
                    className='block absolute h-0.5 w-5 bg-gray-125 transform transition duration-500 ease-in-out opacity-0'
                  />
                  <span
                    aria-hidden='true'
                    className='block absolute h-0.5 w-5 bg-gray-125 transform transition duration-500 ease-in-out -rotate-45'
                  />
                </button>
              </div>
              {menu.showcaseEntries?.map((showcase: any) =>
                generateSubMenuSectionShowcases(
                  showcase,
                  menu.showcaseMobile,
                  localizationContext
                )
              )}
              {menu.subMenus.map((subMenu: any) =>
                generateSubMenuSections(
                  menu.label,
                  subMenu,
                  localizationContext
                )
              )}
              {menu.sectionCTA && (
                <SectionCTA {...menu.sectionCTA}></SectionCTA>
              )}
              {menu.sectionLink && (
                <div className='px-4 mb-6'>
                  <TextLink
                    href={localizeLink(
                      menu.sectionLink.url,
                      localizationContext.language,
                      localizationContext
                    )}
                    icon='arrow'
                    text={menu.sectionLink.label}
                  />
                </div>
              )}
              <DemoCTA
                demoUrl={localizeLink(
                  navigationData.demoUrl,
                  localizationContext.language,
                  localizationContext
                )}
                loginUrl={navigationData.loginUrl}
                restaurantUrl={navigationData.restaurantUrl}
                showTablet={false}
                siteRegion={navigationData.siteRegion}
              />
            </nav>
          )
        }
      })}
    </>
  )
}
